import { useAppSelector } from 'app/store';
import { selectAuth } from 'app/store/authSlice';

export interface PasskeyManagementAccess {
  /** Show the Security surface (tab / menu entry) and allow its route. */
  canManagePasskeys: boolean;
}

/**
 * SEE-226: whether to expose passkey management at all.
 *
 * Hidden for SSO users, using the SAME rule as the post-login prompt
 * (isEligibleForPasskeyPrompt → `!user.identities`), so the two surfaces can
 * never disagree about who is an SSO user. Decided deliberately: an IdP-linked
 * account is managed through the organisation, and the backend refuses passkey
 * registration for it, so the page has nothing to offer.
 *
 * Note the consequence, since it is not obvious: `identities` marks account↔IdP
 * LINKAGE rather than how this session signed in, so a linked user who signs in
 * with a password is also hidden from the page — including from revoking a
 * passkey they enrolled before the account was federated. That is accepted; the
 * alternative (gating on the per-session `custom:original_sub`) would show the
 * page to password sessions of linked accounts and diverge from the prompt.
 */
export function usePasskeyManagementAccess(): PasskeyManagementAccess {
  const { user } = useAppSelector(selectAuth);
  return { canManagePasskeys: !user?.identities };
}

export default usePasskeyManagementAccess;
